/**
 * Typed REST client. Every call parses its response with the shared zod schema
 * and throws an AppError carrying an ErrorCode on failure. Attaches the bearer
 * token from the session store and sends credentials: 'include' so the
 * sr_media cookie (WS-4) flows on join.
 *
 * Schemas and types are imported from @syncreel/shared — never redefined here.
 * We do not import zod directly: it is a transitive dependency of
 * @syncreel/shared, not a declared dependency of @syncreel/web, so we only use
 * the shared schema values (which expose `.parse`).
 */
import {
  AppError,
  ErrorCode,
  GetHealthResponseSchema,
  GetMediaDetailResponseSchema,
  GetMediaListResponseSchema,
  GetReadyResponseSchema,
  PostJoinRoomRequestSchema,
  PostJoinRoomResponseSchema,
  PostRoomsRequestSchema,
  PostRoomsResponseSchema,
  PostJoinByCodeRequestSchema,
  PostJoinByCodeResponseSchema,
  PostSessionRequestSchema,
  PostSessionResponseSchema,
  PostRevokeSessionResponseSchema,
  GetActiveRoomsResponseSchema,
  GetJobsResponseSchema,
  KickMemberResponseSchema,
  BlacklistResponseSchema,
  DeleteRoomResponseSchema,
  DeleteMediaResponseSchema,
  GetLibraryResponseSchema,
  GetDownloadsResponseSchema,
  DownloadSchema,
  PostUploadResponseSchema,
  PostLibraryAddResponseSchema,
  RoomSubtitleTrackSchema,
  type GetHealthResponse,
  type GetMediaDetailResponse,
  type GetMediaListResponse,
  type GetReadyResponse,
  type MediaStatus,
  type PostJoinRoomRequest,
  type PostJoinRoomResponse,
  type PostRoomsRequest,
  type PostRoomsResponse,
  type PostJoinByCodeRequest,
  type PostJoinByCodeResponse,
  type PostSessionRequest,
  type PostSessionResponse,
  type PostRevokeSessionResponse,
  type GetActiveRoomsResponse,
  type GetJobsResponse,
  type KickMemberResponse,
  type BlacklistResponse,
  type DeleteRoomResponse,
  type DeleteMediaResponse,
  type GetLibraryResponse,
  type GetDownloadsResponse,
  type Download,
  type PostUploadResponse,
  type PostLibraryAddResponse,
  type RoomSubtitleTrack,
} from '@syncreel/shared';
import { useSessionStore } from '../store/session';
import type { QueryParams } from './types';
import { logger } from './logger';

/** Set of valid ErrorCode values, for validating error envelopes without zod. */
const ERROR_CODES: ReadonlySet<string> = new Set(Object.values(ErrorCode));

/** Build a query string from a params record, skipping undefined values. */
function toQueryString(params: QueryParams): string {
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) sp.set(key, String(value));
  }
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}

/** Read the bearer token from the session store (works outside React). */
function bearerHeaders(): Record<string, string> {
  const token = useSessionStore.getState().token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Clear the session on 401 (token expired/invalid) so the user re-authenticates. */
function handleUnauthorized(): void {
  useSessionStore.getState().clearSession();
}

/**
 * Parse an error response body into an AppError. Falls back to E_INTERNAL when
 * the body is not a recognizable envelope (no zod dependency required).
 */
async function parseError(res: Response): Promise<AppError> {
  let code = ErrorCode.E_INTERNAL;
  let message = `Request failed (${res.status}).`;
  try {
    const body = (await res.json()) as { error?: { code?: unknown; message?: unknown } };
    const env = body?.error;
    if (env && typeof env.code === 'string' && ERROR_CODES.has(env.code)) {
      code = env.code as ErrorCode;
    }
    if (env && typeof env.message === 'string') {
      message = env.message;
    }
  } catch {
    logger.warn('unparseable error envelope', { status: res.status });
  }
  return new AppError(code, message, { status: res.status });
}

/** Parsed JSON result (or undefined for 204). */
type Json = unknown;

/**
 * Core request helper. Returns parsed JSON on success (caller validates with a
 * shared schema) and throws a typed AppError on failure.
 */
async function request(
  method: string,
  path: string,
  body?: unknown,
  extraHeaders?: Record<string, string>,
): Promise<Json | undefined> {
  const headers: Record<string, string> = { Accept: 'application/json', ...bearerHeaders(), ...extraHeaders };
  let payload: BodyInit | null = null;
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    payload = JSON.stringify(body);
  }

  let res: Response;
  try {
    // WHY: exactOptionalPropertyTypes — build init conditionally so body is
    // only present when non-null, not passed as undefined.
    const init: RequestInit = { method, headers, credentials: 'include' };
    if (payload !== null) init.body = payload;
    res = await fetch(path, init);
  } catch (err) {
    // Network/transport failure — no envelope to parse.
    throw new AppError(ErrorCode.E_INTERNAL, 'Network request failed.', { cause: err });
  }

  if (res.status === 401) {
    handleUnauthorized();
  }

  if (res.status === 204) {
    return undefined;
  }

  if (!res.ok) {
    throw await parseError(res);
  }

  try {
    return await res.json();
  } catch (err) {
    throw new AppError(ErrorCode.E_INTERNAL, 'Invalid JSON response.', { cause: err });
  }
}

/** Validate `json` with a shared zod schema, throwing E_INTERNAL on mismatch. */
function validate<T>(schema: { parse: (input: unknown) => T }, json: unknown): T {
  try {
    return schema.parse(json);
  } catch (err) {
    // WHY: a schema mismatch means the server returned an unexpected shape;
    // surface as internal rather than leaking zod internals to the UI.
    logger.error('response schema mismatch', err);
    throw new AppError(ErrorCode.E_INTERNAL, 'Unexpected response shape.', { cause: err });
  }
}

// ── Health / Ready ──────────────────────────────────────────────────────────

/** GET /api/health — unauthenticated liveness probe. */
export async function getHealth(): Promise<GetHealthResponse> {
  return validate<GetHealthResponse>(GetHealthResponseSchema, await request('GET', '/api/health'));
}

/** GET /api/ready — unauthenticated readiness probe. */
export async function getReady(): Promise<GetReadyResponse> {
  return validate<GetReadyResponse>(GetReadyResponseSchema, await request('GET', '/api/ready'));
}

// ── Auth ────────────────────────────────────────────────────────────────────

/** POST /api/auth/session — issue an anonymous session token. */
export async function postSession(body: PostSessionRequest): Promise<PostSessionResponse> {
  // Validate locally before the round-trip; server re-validates too.
  void PostSessionRequestSchema.parse(body);
  return validate<PostSessionResponse>(
    PostSessionResponseSchema,
    await request('POST', '/api/auth/session', body),
  );
}

/** POST /api/auth/revoke — revoke the caller's current session token. */
export async function revokeSession(): Promise<PostRevokeSessionResponse> {
  return validate<PostRevokeSessionResponse>(
    PostRevokeSessionResponseSchema,
    await request('POST', '/api/auth/revoke'),
  );
}

// ── Media ────────────────────────────────────────────────────────────────────

/** GET /api/media — list media summaries with optional filters. */
export async function getMediaList(query?: {
  q?: string;
  limit?: number;
  offset?: number;
  status?: MediaStatus;
  adminToken?: string;
}): Promise<GetMediaListResponse> {
  const qs = toQueryString({
    q: query?.q,
    limit: query?.limit,
    offset: query?.offset,
    status: query?.status,
  });
  const headers = query?.adminToken ? { 'X-Admin-Token': query.adminToken } : undefined;
  return validate<GetMediaListResponse>(
    GetMediaListResponseSchema,
    await request('GET', `/api/media${qs}`, undefined, headers),
  );
}

/** GET /api/media/:mediaId — full media detail. */
export async function getMediaDetail(mediaId: string): Promise<GetMediaDetailResponse> {
  return validate<GetMediaDetailResponse>(
    GetMediaDetailResponseSchema,
    await request('GET', `/api/media/${encodeURIComponent(mediaId)}`),
  );
}

// ── Rooms ───────────────────────────────────────────────────────────────────

/** POST /api/rooms — create a room (caller becomes host). Admin-only. */
export async function postRoom(
  body: PostRoomsRequest,
  adminToken?: string,
): Promise<PostRoomsResponse> {
  void PostRoomsRequestSchema.parse(body);
  const headers = adminToken ? { 'X-Admin-Token': adminToken } : undefined;
  return validate<PostRoomsResponse>(
    PostRoomsResponseSchema,
    await request('POST', '/api/rooms', body, headers),
  );
}

/** POST /api/rooms/join-by-code — resolve an invitation code to a room summary.
 * No admin token required; any authenticated user may call it. */
export async function joinRoomByCode(
  body: PostJoinByCodeRequest,
): Promise<PostJoinByCodeResponse> {
  void PostJoinByCodeRequestSchema.parse(body);
  return validate<PostJoinByCodeResponse>(
    PostJoinByCodeResponseSchema,
    await request('POST', '/api/rooms/join-by-code', body),
  );
}

/** POST /api/rooms/:roomId/join — join a room; mints the sr_media cookie. */
export async function postJoinRoom(
  roomId: string,
  body: PostJoinRoomRequest,
): Promise<PostJoinRoomResponse> {
  void PostJoinRoomRequestSchema.parse(body);
  return validate<PostJoinRoomResponse>(
    PostJoinRoomResponseSchema,
    await request('POST', `/api/rooms/${encodeURIComponent(roomId)}/join`, body),
  );
}

/** GET /api/rooms/:roomId/media-token — refresh the sr_media cookie (204). */
export async function refreshMediaToken(roomId: string): Promise<void> {
  await request('GET', `/api/rooms/${encodeURIComponent(roomId)}/media-token`);
}

// ── Host management ────────────────────────────────────────────────────────

/** GET /api/rooms — list active rooms (admin home screen). Requires admin token. */
export async function getActiveRooms(): Promise<GetActiveRoomsResponse> {
  const adminToken = useSessionStore.getState().adminToken;
  const headers = adminToken ? { 'X-Admin-Token': adminToken } : undefined;
  return validate<GetActiveRoomsResponse>(
    GetActiveRoomsResponseSchema,
    await request('GET', '/api/rooms', undefined, headers),
  );
}

/** DELETE /api/rooms/:roomId/members/:userId — kick a member. */
export async function kickMember(roomId: string, userId: string): Promise<KickMemberResponse> {
  return validate<KickMemberResponse>(
    KickMemberResponseSchema,
    await request('DELETE', `/api/rooms/${encodeURIComponent(roomId)}/members/${encodeURIComponent(userId)}`),
  );
}

/** POST /api/rooms/:roomId/blacklist/:userId — blacklist a member's IP. */
export async function blacklistMember(roomId: string, userId: string): Promise<BlacklistResponse> {
  return validate<BlacklistResponse>(
    BlacklistResponseSchema,
    await request('POST', `/api/rooms/${encodeURIComponent(roomId)}/blacklist/${encodeURIComponent(userId)}`),
  );
}

/** DELETE /api/rooms/:roomId — terminate a room (host only). */
export async function deleteRoom(roomId: string): Promise<DeleteRoomResponse> {
  return validate<DeleteRoomResponse>(
    DeleteRoomResponseSchema,
    await request('DELETE', `/api/rooms/${encodeURIComponent(roomId)}`),
  );
}

/**
 * POST /api/rooms/:roomId/subtitles — host uploads a custom subtitle track.
 *
 * The subtitle text is sent as text/plain (not JSON) so it can exceed the
 * global 32 KB JSON body limit. Label and language travel as query params.
 * The server converts SRT → VTT, stores the file, and broadcasts the new
 * track to all room members via the `room:subtitle-added` socket event.
 */
export async function uploadSubtitle(
  roomId: string,
  text: string,
  opts: { label?: string; language?: string } = {},
): Promise<RoomSubtitleTrack> {
  const qs = toQueryString({ label: opts.label, language: opts.language });
  const headers: Record<string, string> = {
    'Content-Type': 'text/plain',
    Accept: 'application/json',
    ...bearerHeaders(),
  };
  let res: Response;
  try {
    res = await fetch(`/api/rooms/${encodeURIComponent(roomId)}/subtitles${qs}`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: text,
    });
  } catch (err) {
    throw new AppError(ErrorCode.E_INTERNAL, 'Network request failed.', { cause: err });
  }
  if (res.status === 401) handleUnauthorized();
  if (!res.ok) throw await parseError(res);
  return validate<RoomSubtitleTrack>(RoomSubtitleTrackSchema, await res.json());
}

// ── Admin ───────────────────────────────────────────────────────────────────

/**
 * GET /api/admin/jobs — verify an admin token by hitting the admin endpoint.
 * Returns the jobs list on success; throws an AppError (E_UNAUTHORIZED) when
 * the token is invalid. Used by the /admin page to gate the catalogue.
 */
export async function verifyAdminToken(adminToken: string): Promise<GetJobsResponse> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Admin-Token': adminToken,
    ...bearerHeaders(),
  };
  let res: Response;
  try {
    res = await fetch('/api/admin/jobs', { method: 'GET', headers, credentials: 'include' });
  } catch (err) {
    throw new AppError(ErrorCode.E_INTERNAL, 'Network request failed.', { cause: err });
  }
  if (res.status === 401) {
    // WHY: do NOT clear the session here — a bad admin token is not a bad
    // session. Just surface the error so the caller can re-prompt.
  }
  if (!res.ok) throw await parseError(res);
  return validate<GetJobsResponse>(GetJobsResponseSchema, await res.json());
}

/**
 * GET /api/admin/jobs — fetch the current transcode queue snapshot.
 * Used by the catalogue to poll progress for transcoding items.
 */
export async function getJobs(adminToken: string): Promise<GetJobsResponse> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Admin-Token': adminToken,
    ...bearerHeaders(),
  };
  let res: Response;
  try {
    res = await fetch('/api/admin/jobs', { method: 'GET', headers, credentials: 'include' });
  } catch (err) {
    throw new AppError(ErrorCode.E_INTERNAL, 'Network request failed.', { cause: err });
  }
  if (!res.ok) throw await parseError(res);
  return validate<GetJobsResponse>(GetJobsResponseSchema, await res.json());
}

/**
 * DELETE /api/admin/media/:mediaId — delete a media item and its HLS output.
 * Requires an admin token. Throws AppError on failure (e.g. active rooms
 * referencing the media, or invalid admin token).
 */
export async function deleteMedia(mediaId: string, adminToken: string): Promise<DeleteMediaResponse> {
  const headers: Record<string, string> = {
    'X-Admin-Token': adminToken,
    ...bearerHeaders(),
  };
  let res: Response;
  try {
    res = await fetch(`/api/admin/media/${encodeURIComponent(mediaId)}`, {
      method: 'DELETE',
      headers,
      credentials: 'include',
    });
  } catch (err) {
    throw new AppError(ErrorCode.E_INTERNAL, 'Network request failed.', { cause: err });
  }
  if (!res.ok) throw await parseError(res);
  return validate<DeleteMediaResponse>(DeleteMediaResponseSchema, await res.json());
}

/** GET /api/admin/library — video files under MEDIA_DIR and whether each is added. */
export async function getLibraryFiles(adminToken: string): Promise<GetLibraryResponse> {
  return validate<GetLibraryResponse>(
    GetLibraryResponseSchema,
    await request('GET', '/api/admin/library', undefined, { 'X-Admin-Token': adminToken }),
  );
}

/** POST /api/admin/library/add — add one source file to the library (queues its transcode). */
export async function addLibraryFile(
  relPath: string,
  adminToken: string,
): Promise<PostLibraryAddResponse> {
  return validate<PostLibraryAddResponse>(
    PostLibraryAddResponseSchema,
    await request('POST', '/api/admin/library/add', { relPath }, { 'X-Admin-Token': adminToken }),
  );
}

/**
 * PUT /api/admin/uploads — stream a video file to the server with progress.
 * Uses XMLHttpRequest because fetch() cannot report upload progress. Abort the
 * returned `signal` controller to cancel (the server discards the partial file).
 */
export function uploadVideoFile(
  file: File,
  adminToken: string,
  onProgress: (fraction: number) => void,
  signal: AbortSignal,
): Promise<PostUploadResponse> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', `/api/admin/uploads?name=${encodeURIComponent(file.name)}`);
    xhr.setRequestHeader('X-Admin-Token', adminToken);
    xhr.setRequestHeader('Content-Type', 'application/octet-stream');
    const bearer = useSessionStore.getState().token;
    if (bearer) xhr.setRequestHeader('Authorization', `Bearer ${bearer}`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && e.total > 0) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      let body: unknown;
      try {
        body = JSON.parse(xhr.responseText);
      } catch {
        body = undefined;
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(validate<PostUploadResponse>(PostUploadResponseSchema, body));
        } catch (err) {
          reject(err);
        }
        return;
      }
      const env = (body as { error?: { code?: unknown; message?: unknown } } | undefined)?.error;
      const code = typeof env?.code === 'string' && ERROR_CODES.has(env.code) ? (env.code as ErrorCode) : ErrorCode.E_INTERNAL;
      const message =
        xhr.status === 413
          ? 'The proxy rejected the file as too large (raise client_max_body_size).'
          : typeof env?.message === 'string'
            ? env.message
            : `Upload failed (${xhr.status}).`;
      reject(new AppError(code, message, { status: xhr.status }));
    };
    xhr.onerror = () => reject(new AppError(ErrorCode.E_INTERNAL, 'Network error during upload.'));
    xhr.onabort = () => reject(new AppError(ErrorCode.E_INTERNAL, 'Upload cancelled.'));
    signal.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.send(file);
  });
}

/** GET /api/admin/downloads — URL downloads, newest first. */
export async function getDownloads(adminToken: string): Promise<GetDownloadsResponse> {
  return validate<GetDownloadsResponse>(
    GetDownloadsResponseSchema,
    await request('GET', '/api/admin/downloads', undefined, { 'X-Admin-Token': adminToken }),
  );
}

/** POST /api/admin/downloads — queue a server-side download from a URL. */
export async function addDownload(url: string, adminToken: string): Promise<Download> {
  return validate<Download>(
    DownloadSchema,
    await request('POST', '/api/admin/downloads', { url }, { 'X-Admin-Token': adminToken }),
  );
}

/** DELETE /api/admin/downloads/:id — cancel a download or clear its record. */
export async function removeDownload(id: string, adminToken: string): Promise<void> {
  await request('DELETE', `/api/admin/downloads/${encodeURIComponent(id)}`, undefined, { 'X-Admin-Token': adminToken });
}
